import type { Edge, Node } from '@xyflow/react';
import type { ListStepTypesResponse } from '@erp/rpc';
import { isStepLayoutGroupNode } from './stepLayoutGroups';
import {
  archiveShipAndDebitAzureFilesStep,
  backgroundAgentStep,
  blobStorageFetchStep,
  classifyDocumentV2Step,
  classifyEmailStep,
  classifyMasterDataStep,
  confidenceThresholdStep,
  coreEnumFanoutStep,
  decisionStep,
  emailRuleTriggerStep,
  eventTriggerStep,
  actionInputStep,
  actionOutputStep,
  FLOW_CONTROL_SOCKET_TYPE,
  FLOW_IN_SOCKET_ID,
  FLOW_OUT_SOCKET_ID,
  logValueStep,
  pipelineRowsInsertStep,
  pipelineRowsQueryStep,
  pipelineRowsUpdateStep,
  randomNumberGeneratorStep,
  scheduleTriggerStep,
  workflowActionStep,
  codeSandboxStep,
  createTypedTaskStep,
  statefulTableLookupRowStep,
  statefulTableReadFrameStep,
  useDataMappingStep,
  managedMapperStep,
  transformE844dFieldsStep,
  fwfDataSourceWriterStep,
  csvDataSourceWriterStep,
  upsertProductStep,
  getProductVariantStep,
  decomposeEntityStep,
  mapValueStep,
  isDecomposableEntityType,
  type DecomposableEntityType,
  startDataSourceLoadStep,
  famousSqlQueryStep,
  generateFamousSqlQueryInputSockets,
  generateFamousSqlQueryOutputSockets,
  generateFanInOutputSockets,
  FAN_IN_INPUT_SOCKET_ID,
  SocketTypeSchema,
  type SocketType,
  generateCodeSandboxInputSockets,
  generateCodeSandboxOutputSockets,
  generateCsvTransformV4InputSockets,
  generateCollectResultsInputSockets,
  generateSocketsFromSchema,
  collectResultsStep,
  generateMatchCustomerWithEvidenceInputSockets,
  matchCustomerWithEvidenceStep,
  generateTaskCreateInputSockets,
  generateTaskCreateOutputSockets,
  isTaskCreateStepType,
  FAMOUS_SQL_QUERY_STEP_TYPE,
  upsertVendorStep,
  upsertVendorRexelStep,
  type ValidationEdge,
  type ValidationStep,
  deriveWorkflowActionInterface,
  actionBoundaryConfigSchema,
  type ActionPort,
  ACTION_INPUT_STEP_TYPE,
  ACTION_OUTPUT_STEP_TYPE,
  buildManualOverrideValidationSteps as buildSharedManualOverrideValidationSteps,
  readManualExpressionOverrides,
  type WorkflowExpressionStep,
  deriveWorkflowExpressionEdges,
} from '@erp/workflows';
import { IOType } from '@erp/shared-types/node-graph';

/**
 * Step types whose sockets are derived from `config` rather than from a
 * fixed Zod schema. Used in:
 *   - WorkflowEditor's `onConfigChange` → regenerate sockets when config changes
 *   - graphToDefinition → persist live sockets on save (instead of the
 *     stale ones that came from the last server response)
 *   - `hydrateStepTypeData` → preserve actual node sockets when re-hydrating
 *     `_stepTypeData` from the API metadata
 *
 * Keep in sync with `hasDynamicOutputSockets`/dispatchers in
 * `backend/src/workflows/sockets/dynamicOutputSockets.ts`.
 */
export type DynamicSocketBehavior = {
  dynamicInputs: boolean;
  dynamicOutputs: boolean;
};

export const DYNAMIC_SOCKET_BEHAVIORS: Record<string, DynamicSocketBehavior> = {
  ai_reasoning: { dynamicInputs: false, dynamicOutputs: true },
  event_trigger: { dynamicInputs: false, dynamicOutputs: true },
  wait_for_event: { dynamicInputs: false, dynamicOutputs: true },
  background_agent: { dynamicInputs: true, dynamicOutputs: true },
  // The `value` input is array-typed when multiMatch is on (so an upstream
  // array feeds the switch whole instead of fanning out) — mirrors backend
  // generateSwitchInputSockets.
  switch: { dynamicInputs: true, dynamicOutputs: true },
  interpolate_email_template: { dynamicInputs: true, dynamicOutputs: false },
  split_document_v2: { dynamicInputs: false, dynamicOutputs: true },
  // Await: outputs come from `config.mode` AND from the type of whatever is
  // wired into `value` (fan_in is generic, `input<T> -> array<T>`), so they are
  // regenerated on a wire change as well as on a config change.
  fan_in: { dynamicInputs: false, dynamicOutputs: true },
  identify_customer: { dynamicInputs: false, dynamicOutputs: true },
  identify_supplier: { dynamicInputs: false, dynamicOutputs: true },
  classify_email: { dynamicInputs: false, dynamicOutputs: true },
  action_input: { dynamicInputs: false, dynamicOutputs: true },
  action_output: { dynamicInputs: true, dynamicOutputs: true },
  workflow_action: { dynamicInputs: true, dynamicOutputs: true },
  code_sandbox: { dynamicInputs: true, dynamicOutputs: true },
  entity_resolver: { dynamicInputs: true, dynamicOutputs: true },
  approval: { dynamicInputs: true, dynamicOutputs: true },
  // Input sockets depend on config.eventType (draft events need the draft id;
  // DISCOUNT_REQUEST_DOCUMENT_ROUTED needs the inbound email + target org instead) —
  // mirrors backend generateEmitSystemEventInputSockets.
  emit_system_event: { dynamicInputs: true, dynamicOutputs: false },
  // Inputs are the static seven plus one per field of every extension registered
  // for ProductV3 — registry state, resolved in the editor and re-derived
  // server-side on save.
  upsert_product: { dynamicInputs: true, dynamicOutputs: false },
  // Same for every inventory-policy extension's fields.
  upsert_inventory_policy_classification: {
    dynamicInputs: true,
    dynamicOutputs: false,
  },
  extract_document_content_v3: { dynamicInputs: false, dynamicOutputs: true },
  extract_document_content_v4: { dynamicInputs: false, dynamicOutputs: true },
  http_trigger: { dynamicInputs: false, dynamicOutputs: true },
  // Output sockets are derived from the configured form fields (pure/sync —
  // mirrors backend generateFormTriggerOutputSockets).
  form_trigger: { dynamicInputs: false, dynamicOutputs: true },
  // Per-column outputs are resolved server-side from the observed link's
  // SchemaDefinition and persisted on save (mirrors http_trigger); the editor
  // must preserve those server sockets rather than reset to the static schema.
  observe_data_source_link: { dynamicInputs: false, dynamicOutputs: true },
  stateful_table_lookup_row: { dynamicInputs: false, dynamicOutputs: true },
  // Per-column filter inputs AND per-column array outputs, both derived from the
  // config column snapshot (mirrors generateStatefulTableReadFrame*Sockets).
  stateful_table_read_frame: { dynamicInputs: true, dynamicOutputs: true },
  // Both directions: the picked match column types the input socket, and
  // requireSingleMatch decides whether the output is a list or one row.
  use_data_mapping: { dynamicInputs: true, dynamicOutputs: true },
  external_http_call: { dynamicInputs: true, dynamicOutputs: true },
  voice_agent: { dynamicInputs: false, dynamicOutputs: true },
  managed_mapper: { dynamicInputs: true, dynamicOutputs: true },
  web_browser: { dynamicInputs: true, dynamicOutputs: true },
  famous_sql_query: { dynamicInputs: true, dynamicOutputs: true },
  csv_transform_v4: { dynamicInputs: true, dynamicOutputs: false },
  // One array input per configured item input, beside the static `results`
  // (mirrors backend generateCollectResultsInputSockets).
  collect_results: { dynamicInputs: true, dynamicOutputs: false },
  // One input per configured evidence entry, typed by its kind (mirrors
  // backend generateMatchCustomerWithEvidenceInputSockets).
  match_customer_with_evidence: { dynamicInputs: true, dynamicOutputs: false },
  // Source/anchor entity sockets become required + typed from the selected
  // task type's declared `anchorEntityType` (mirrors generateTaskCreateInputSockets).
  create_task: { dynamicInputs: true, dynamicOutputs: false },
  // Task type is config-only, so the type's input-schema fields are required
  // sockets (mirrors generateTaskCreateInputSockets for create_typed_task).
  create_typed_task: { dynamicInputs: true, dynamicOutputs: false },
  generate_task: { dynamicInputs: true, dynamicOutputs: false },
  create_subtask: { dynamicInputs: true, dynamicOutputs: false },
  // listMode retypes the id input to array<uuid> and collapses outputs to one
  // variants array. returnEmptyIfNotFound makes productId optional. Mirrors
  // generateGetProductVariant*Sockets.
  get_product_variant: { dynamicInputs: true, dynamicOutputs: true },
  // Outputs are the ENTITY_DECOMPOSITIONS fields of config.entityType, which the
  // editor sets from the wired entity's socket type (syncDecomposeEntitySockets).
  decompose_entity: { dynamicInputs: false, dynamicOutputs: true },
};

export const STEP_CONFIG_SCHEMAS: Record<
  string,
  {
    safeParse: (data: unknown) => {
      success: boolean;
      data?: unknown;
      error?: { issues: Array<{ path: PropertyKey[]; message: string }> };
    };
  }
> = {
  event_trigger: eventTriggerStep.configSchema,
  email_rule_trigger: emailRuleTriggerStep.configSchema,
  action_input: actionInputStep.configSchema,
  action_output: actionOutputStep.configSchema,
  workflow_action: workflowActionStep.configSchema,
  schedule_trigger: scheduleTriggerStep.configSchema,
  background_agent: backgroundAgentStep.configSchema,
  confidence_threshold: confidenceThresholdStep.configSchema,
  decision: decisionStep.configSchema,
  blob_storage_fetch: blobStorageFetchStep.configSchema,
  core_enum_fanout: coreEnumFanoutStep.configSchema,
  pipeline_rows_query: pipelineRowsQueryStep.configSchema,
  pipeline_rows_update: pipelineRowsUpdateStep.configSchema,
  pipeline_rows_insert: pipelineRowsInsertStep.configSchema,
  random_number_generator: randomNumberGeneratorStep.configSchema,
  log_value: logValueStep.configSchema,
  classify_email: classifyEmailStep.configSchema,
  classify_document_v2: classifyDocumentV2Step.configSchema,
  classify_master_data: classifyMasterDataStep.configSchema,
  archive_ship_and_debit_azure_files:
    archiveShipAndDebitAzureFilesStep.configSchema,
  transform_e844d_fields: transformE844dFieldsStep.configSchema,
  code_sandbox: codeSandboxStep.configSchema,
  create_typed_task: createTypedTaskStep.configSchema,
  stateful_table_lookup_row: statefulTableLookupRowStep.configSchema,
  stateful_table_read_frame: statefulTableReadFrameStep.configSchema,
  use_data_mapping: useDataMappingStep.configSchema,
  managed_mapper: managedMapperStep.configSchema,
  fwf_datasource_writer: fwfDataSourceWriterStep.configSchema,
  csv_datasource_writer: csvDataSourceWriterStep.configSchema,
  collect_results: collectResultsStep.configSchema,
  upsert_product: upsertProductStep.configSchema,
  famous_sql_query: famousSqlQueryStep.configSchema,
  upsert_vendor: upsertVendorStep.configSchema,
  upsert_vendor_rexel: upsertVendorRexelStep.configSchema,
  get_product_variant: getProductVariantStep.configSchema,
  decompose_entity: decomposeEntityStep.configSchema,
  map_value: mapValueStep.configSchema,
  start_data_source_load: startDataSourceLoadStep.configSchema,
};

export function getSocketTypeFromNode(
  node: Node,
  handleId: string | null | undefined,
  direction: 'input' | 'output',
): string {
  if (!handleId) return 'any';

  if (handleId === FLOW_IN_SOCKET_ID || handleId === FLOW_OUT_SOCKET_ID) {
    return FLOW_CONTROL_SOCKET_TYPE;
  }

  const nodeData = node.data as {
    inputs?: Record<string, { id: string; socketType?: string }>;
    outputs?: Record<string, { id: string; socketType?: string }>;
  };

  const sockets = direction === 'input' ? nodeData.inputs : nodeData.outputs;
  if (!sockets) return 'any';

  const socket = Object.values(sockets).find((entry) => entry.id === handleId);
  return socket?.socketType || 'any';
}

export const DECOMPOSE_ENTITY_STEP_TYPE = 'decompose_entity';
export const DECOMPOSE_ENTITY_INPUT_SOCKET_ID = 'entity';

const TYPED_ENTITY_REFERENCE = /^entity_reference:([A-Z_]+)$/;

/**
 * The entity type wired into a Decompose Entity node's `entity` input, read
 * from the source socket's `entity_reference:<TYPE>` type. `null` when nothing
 * is wired, the source is an untyped entity reference, or the type has no
 * decomposition — the node then keeps whatever type its config already has.
 */
export function decomposeEntityTypeFromWire(
  nodeId: string,
  nodes: Node[],
  edges: Edge[],
): DecomposableEntityType | null {
  const incoming = edges.find(
    (edge) =>
      edge.target === nodeId &&
      edge.targetHandle === DECOMPOSE_ENTITY_INPUT_SOCKET_ID,
  );
  if (!incoming) return null;
  const source = nodes.find((node) => node.id === incoming.source);
  if (!source) return null;
  const socketType = getSocketTypeFromNode(
    source,
    incoming.sourceHandle,
    'output',
  );
  const entityType = TYPED_ENTITY_REFERENCE.exec(socketType)?.[1];
  return isDecomposableEntityType(entityType) ? entityType : null;
}

function nodeHasSocket(
  node: Node,
  handleId: string | null | undefined,
  direction: 'input' | 'output',
): boolean {
  // An edge with no handle isn't socket-bound, so it can't be orphaned.
  if (!handleId) return true;
  // Flow-control sockets are implicit and always present.
  if (handleId === FLOW_IN_SOCKET_ID || handleId === FLOW_OUT_SOCKET_ID) {
    return true;
  }

  const nodeData = node.data as {
    inputs?: Record<string, { id: string }>;
    outputs?: Record<string, { id: string }>;
  };
  const sockets = direction === 'input' ? nodeData.inputs : nodeData.outputs;
  if (!sockets) return false;
  return Object.values(sockets).some((socket) => socket.id === handleId);
}

/**
 * Edges pointing at a socket that no longer exists on their node — e.g. after a
 * schema version change dropped fields from an External HTTP Call step.
 *
 * React Flow can't draw an edge whose handle is gone, so these are invisible on
 * the canvas AND unselectable: the user can neither see nor delete them, while
 * publish validation still blocks on them. Surfacing them is what makes that
 * dead end escapable. Edges whose *node* is missing are excluded — those are
 * already dropped by `definitionToGraph`.
 */
export function findOrphanSocketEdges(nodes: Node[], edges: Edge[]): Edge[] {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));

  return edges.filter((edge) => {
    const sourceNode = nodeById.get(edge.source);
    const targetNode = nodeById.get(edge.target);
    if (!sourceNode || !targetNode) return false;
    return (
      !nodeHasSocket(sourceNode, edge.sourceHandle, 'output') ||
      !nodeHasSocket(targetNode, edge.targetHandle, 'input')
    );
  });
}

/**
 * Socket record written by `mapSockets` / config-derived reconciliation.
 * `ioType` is the string enum (not a raw number) so the result assigns onto
 * `StepNodeData.inputs` / `outputs`.
 */
export interface MappedNodeSocket {
  id: string;
  name: string;
  ioType: IOType;
  socketType: string;
  required?: boolean;
}

export function mapSockets(
  sockets: Array<{
    id: string;
    name: string;
    type: string;
    required?: boolean;
  }>,
  ioType: IOType,
): Record<string, MappedNodeSocket> {
  return Object.fromEntries(
    sockets.map((socket) => [
      socket.id,
      {
        id: socket.id,
        name: socket.name,
        ioType,
        socketType: socket.type,
        required: socket.required,
      },
    ]),
  );
}

/**
 * Socket record entry as stored on editor node data (`data.inputs` /
 * `data.outputs`). Persisted graphs may still carry a numeric `ioType`.
 */
export interface NodeSocketRecordEntry {
  id: string;
  /**
   * Display label. Held on the entry rather than read from the record key,
   * because the key is the socket id: two sockets can share a display name
   * (a switch case labelled `default` alongside the fixed `default` socket),
   * and keying by name silently collapsed them to one.
   */
  name: string;
  ioType: IOType | number;
  socketType?: string;
  required?: boolean;
}

type StepTypeData = ListStepTypesResponse['stepTypes'][0];

/**
 * Hydrate step-type metadata for a node, preserving the node's actual
 * (DB-stored) sockets for steps with dynamic sockets instead of the base
 * schema sockets from the API.
 *
 * The `required` flag must come from the stored socket: dynamic input
 * sockets are frequently optional (e.g. stateful_table_read_frame's
 * per-column filters are ALL optional). Hardcoding `required: true` here
 * made the editor render optional inputs with a required marker and —
 * because `nodesToValidationSteps` reads these hydrated sockets — blocked
 * Save & Publish with "missing required inputs" for any read-frame node
 * with unwired filters.
 *
 * Empty live sockets are also authoritative for dynamic-input steps: falling
 * back to the palette's schema-derived sockets reintroduced a bogus required
 * "Value" input for z.record()-backed steps (famous_sql_query, code_sandbox)
 * when no bind/input keys were declared yet.
 */
export function hydrateDynamicStepTypeData(
  stData: StepTypeData,
  stepType: string,
  inputs: Record<string, NodeSocketRecordEntry>,
  outputs: Record<string, NodeSocketRecordEntry>,
): StepTypeData {
  const behavior = DYNAMIC_SOCKET_BEHAVIORS[stepType];
  if (!behavior) return stData;

  const nodeInputSockets = Object.entries(inputs);
  const nodeOutputSockets = Object.entries(outputs);

  const hydrated: StepTypeData = { ...stData };

  // `name` comes off the entry, never off the record key: the key is the socket
  // id, so reading the label from it would rewrite every dynamic socket's
  // display name to its id — and `graphToDefinition` prefers hydrated
  // `_stepTypeData`, so the next save would persist the id as the name. The key
  // remains a fallback for entries built before the label was carried.
  if (behavior.dynamicInputs) {
    hydrated.inputSockets = nodeInputSockets.map(([key, socket]) => ({
      id: socket.id,
      name: socket.name || key,
      type: socket.socketType ?? 'any',
      required: socket.required ?? true,
    }));
  }

  if (behavior.dynamicOutputs && nodeOutputSockets.length > 0) {
    hydrated.outputSockets = nodeOutputSockets.map(([key, socket]) => ({
      id: socket.id,
      name: socket.name || key,
      type: socket.socketType ?? 'any',
      required: true,
    }));
  }

  return hydrated;
}

/**
 * Rebuild sockets for steps whose inputs/outputs are fully config-derived,
 * so a stale palette "Value" socket (from the old z.record() schema fallback)
 * cannot stick on the node and block Save & Publish.
 *
 * Returns null when the step type does not need this reconciliation.
 */
/**
 * Emit System Event's input sockets, keyed on the configured event type:
 * DISCOUNT_REQUEST_DOCUMENT_ROUTED routes an inbound email (+ required target org), the
 * draft events route a draft (+ optional target org / email id). Mirrors
 * backend generateEmitSystemEventInputSockets. Shared by socket
 * reconciliation below and the editor's config-change regeneration.
 */
export function emitSystemEventInputSockets(eventType: unknown): Array<{
  id: string;
  name: string;
  type: string;
  required: boolean;
}> {
  if (eventType === 'DISCOUNT_REQUEST_DOCUMENT_ROUTED') {
    return [
      {
        id: 'inboundEmailId',
        name: 'Inbound Email Id',
        type: 'uuid',
        required: true,
      },
      {
        id: 'targetOrganizationId',
        name: 'Target Organization Id',
        type: 'uuid',
        required: true,
      },
    ];
  }
  return [
    {
      id: 'draftSalesOrderId',
      name: 'Draft Sales Order Id',
      type: 'uuid',
      required: true,
    },
    {
      id: 'targetOrganizationId',
      name: 'Target Organization Id',
      type: 'uuid',
      required: false,
    },
    {
      id: 'inboundEmailId',
      name: 'Inbound Email Id',
      type: 'uuid',
      required: false,
    },
  ];
}

// fallow-ignore-next-line complexity
export function reconcileConfigDerivedSockets(
  stepType: string,
  config: Record<string, unknown> | undefined,
): {
  inputSockets: Array<{
    id: string;
    name: string;
    type: string;
    required: boolean;
    description?: string;
  }>;
  outputSockets: Array<{
    id: string;
    name: string;
    type: string;
    required: boolean;
    description?: string;
  }>;
} | null {
  const safeConfig = config ?? {};

  if (stepType === FAMOUS_SQL_QUERY_STEP_TYPE) {
    return {
      inputSockets: generateFamousSqlQueryInputSockets(safeConfig),
      outputSockets: generateFamousSqlQueryOutputSockets(safeConfig),
    };
  }

  // Emit System Event: input sockets are keyed on the configured event type.
  // Reconciling here heals nodes whose persisted sockets predate the config
  // (e.g. sockets generated before an event type was picked).
  if (stepType === 'emit_system_event') {
    return {
      inputSockets: emitSystemEventInputSockets(safeConfig.eventType),
      outputSockets: [
        { id: 'emitted', name: 'Emitted', type: 'boolean', required: true },
        { id: 'eventType', name: 'Event Type', type: 'string', required: true },
      ],
    };
  }

  if (stepType === 'csv_transform_v4') {
    return {
      inputSockets: generateCsvTransformV4InputSockets(safeConfig),
      outputSockets: [],
    };
  }

  if (stepType === 'collect_results') {
    return {
      inputSockets: generateCollectResultsInputSockets(safeConfig),
      outputSockets: generateSocketsFromSchema(
        collectResultsStep.outputSchema,
        'output',
      ),
    };
  }

  if (stepType === matchCustomerWithEvidenceStep.type) {
    return {
      inputSockets: generateMatchCustomerWithEvidenceInputSockets(safeConfig),
      outputSockets: generateSocketsFromSchema(
        matchCustomerWithEvidenceStep.outputSchema,
        'output',
      ),
    };
  }

  if (stepType === 'code_sandbox') {
    return {
      inputSockets: generateCodeSandboxInputSockets(safeConfig),
      outputSockets: generateCodeSandboxOutputSockets(safeConfig),
    };
  }

  if (isTaskCreateStepType(stepType)) {
    return {
      inputSockets: generateTaskCreateInputSockets(stepType, safeConfig),
      outputSockets: generateTaskCreateOutputSockets(stepType),
    };
  }

  return null;
}

export type ReconciledConfigSockets = NonNullable<
  ReturnType<typeof reconcileConfigDerivedSockets>
>;

/** Map generator sockets onto the node `inputs`/`outputs` records the canvas stores. */
export function stepNodeSocketsFromReconciled(
  reconciled: ReconciledConfigSockets,
): {
  inputs: Record<string, MappedNodeSocket>;
  outputs: Record<string, MappedNodeSocket>;
} {
  return {
    inputs: mapSockets(reconciled.inputSockets, IOType.INPUT),
    outputs: mapSockets(reconciled.outputSockets, IOType.OUTPUT),
  };
}

/** Offset applied to a duplicated node so it doesn't sit exactly on the source. */
export const DUPLICATE_NODE_OFFSET = 40;

/**
 * Produce the clone for the "duplicate step" action: a fresh id, a position
 * offset from the source, cleared selection, and a deep-copied `config` so
 * edits to the copy never mutate the original. Sockets and step-type metadata
 * are carried over verbatim (a duplicate is an exact copy of the step).
 *
 * Edges are intentionally NOT part of this — a duplicated step starts unwired.
 * Editor-injected `data` fields (callbacks, validation flags) ride along and
 * are re-applied on the next render, so they're harmless here.
 */
/**
 * Merge a canvas static-default value into a step's `config.__static_inputs`
 * record. Empty values (undefined, '', []) clear the socket's entry; when the
 * record empties out, the key is dropped entirely so saved configs stay clean.
 */
export function mergeStaticInputConfig(
  config: Record<string, unknown> | undefined,
  socketId: string,
  value: unknown,
): Record<string, unknown> {
  const nextConfig = { ...(config ?? {}) };
  const existing = nextConfig['__static_inputs'];
  const staticInputs: Record<string, unknown> =
    existing && typeof existing === 'object' && !Array.isArray(existing)
      ? { ...(existing as Record<string, unknown>) }
      : {};
  const empty =
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0);
  if (empty) delete staticInputs[socketId];
  else staticInputs[socketId] = value;
  if (Object.keys(staticInputs).length === 0) {
    delete nextConfig['__static_inputs'];
  } else {
    nextConfig['__static_inputs'] = staticInputs;
  }
  return nextConfig;
}

export function updateStepNodeConfig<
  NodeT extends Node<{ config?: Record<string, unknown> }>,
>(
  nodes: NodeT[],
  nodeId: string,
  updateConfig: (
    config: Record<string, unknown> | undefined,
  ) => Record<string, unknown>,
): NodeT[] {
  return nodes.map((node) =>
    node.id === nodeId
      ? {
          ...node,
          data: { ...node.data, config: updateConfig(node.data.config) },
        }
      : node,
  );
}

export function cloneNodeForDuplication<NodeT extends Node>(
  node: NodeT,
  newId: string,
): NodeT {
  const data = node.data as Record<string, unknown>;
  const clonedData = {
    ...data,
    config: data.config
      ? (JSON.parse(JSON.stringify(data.config)) as unknown)
      : data.config,
  };
  return {
    ...node,
    id: newId,
    position: {
      x: (node.position?.x ?? 0) + DUPLICATE_NODE_OFFSET,
      y: (node.position?.y ?? 0) + DUPLICATE_NODE_OFFSET,
    },
    selected: false,
    data: clonedData as NodeT['data'],
  };
}

/**
 * Node data shape used by the step-level "refresh" action — a narrow read view
 * of `StepNodeData`, kept local so this helper doesn't depend on the
 * config-renderer types.
 */
interface EditableNodeData {
  label?: string;
  stepType?: string;
  config?: Record<string, unknown>;
  inputs?: Record<string, NodeSocketRecordEntry>;
  outputs?: Record<string, NodeSocketRecordEntry>;
  _stepTypeData?: StepTypeData;
}

export interface RefreshNodeResult {
  /** The node with sockets + step-type metadata re-derived from the registry. */
  node: Node;
  /** Input socket ids valid after the refresh (for pruning orphaned edges). */
  inputSocketIds: Set<string>;
  /** Output socket ids valid after the refresh (for pruning orphaned edges). */
  outputSocketIds: Set<string>;
}

/**
 * Re-derive a node's sockets and step-type metadata from the latest registry
 * definition (`listStepTypes`) for the "refresh step" action, while preserving
 * user-owned data — `config`, `label`, `id`, and `position`.
 *
 * This exists because a node's sockets are a snapshot taken when it was added:
 * if a step's Zod schema later gains/renames/drops a socket, existing nodes keep
 * the stale shape. Refresh pulls the current shape without recreating the node.
 *
 * Dynamic-socket sides (see `DYNAMIC_SOCKET_BEHAVIORS`) are preserved rather than
 * reset: their real sockets come from `config`, not the base schema, so the
 * registry's base sockets would wipe them (e.g. a switch's per-case outputs).
 * Callers should prune now-orphaned edges with `pruneEdgesForNodeSockets`.
 */
export function refreshNodeFromStepType(
  node: Node,
  latestStepType: StepTypeData,
): RefreshNodeResult {
  const data = node.data as EditableNodeData;
  const stepType = data.stepType ?? '';
  const behavior = DYNAMIC_SOCKET_BEHAVIORS[stepType];

  const nextInputs: Record<string, NodeSocketRecordEntry> =
    behavior?.dynamicInputs
      ? (data.inputs ?? {})
      : mapSockets(latestStepType.inputSockets, IOType.INPUT);
  const nextOutputs: Record<string, NodeSocketRecordEntry> =
    behavior?.dynamicOutputs
      ? (data.outputs ?? {})
      : mapSockets(latestStepType.outputSockets, IOType.OUTPUT);

  const nextStepTypeData = hydrateDynamicStepTypeData(
    latestStepType,
    stepType,
    nextInputs,
    nextOutputs,
  );

  return {
    node: {
      ...node,
      data: {
        ...node.data,
        // config + label are user-owned; preserve them across the refresh.
        inputs: nextInputs,
        outputs: nextOutputs,
        _stepTypeData: nextStepTypeData,
        // Signal graphToDefinition to persist these live sockets on save. For
        // static-schema steps it otherwise keeps the previously-saved sockets
        // (only dynamic-socket types persist live), so the refresh would be
        // lost on reload without this marker.
        __socketsRefreshed: true,
      },
    },
    inputSocketIds: new Set(Object.values(nextInputs).map((s) => s.id)),
    outputSocketIds: new Set(Object.values(nextOutputs).map((s) => s.id)),
  };
}

export interface PruneEdgesResult {
  edges: Edge[];
  /** Edges dropped because their socket on `nodeId` no longer exists. */
  removedEdges: Edge[];
  removedIncoming: number;
  removedOutgoing: number;
}

/**
 * Drop edges attached to a node whose socket no longer exists after a refresh
 * (or any socket change). Flow-control edges are never touched — those handles
 * live outside the data-socket records. Edges to/from other nodes are kept.
 */
export function pruneEdgesForNodeSockets(
  edges: Edge[],
  nodeId: string,
  validInputSocketIds: Set<string>,
  validOutputSocketIds: Set<string>,
): PruneEdgesResult {
  let removedIncoming = 0;
  let removedOutgoing = 0;
  const removedEdges: Edge[] = [];

  const kept = edges.filter((edge) => {
    if (
      edge.target === nodeId &&
      edge.targetHandle &&
      edge.targetHandle !== FLOW_IN_SOCKET_ID &&
      !validInputSocketIds.has(edge.targetHandle)
    ) {
      removedIncoming += 1;
      removedEdges.push(edge);
      return false;
    }
    if (
      edge.source === nodeId &&
      edge.sourceHandle &&
      edge.sourceHandle !== FLOW_OUT_SOCKET_ID &&
      !validOutputSocketIds.has(edge.sourceHandle)
    ) {
      removedOutgoing += 1;
      removedEdges.push(edge);
      return false;
    }
    return true;
  });

  return { edges: kept, removedEdges, removedIncoming, removedOutgoing };
}

/**
 * Drop the outgoing edges of Decompose Entity nodes whose output socket no
 * longer exists — wiring a different entity type replaces the node's outputs,
 * and an edge left on a vanished socket is invisible and unselectable.
 */
export function pruneDecomposeEntityOutputEdges(
  nodes: Node[],
  edges: Edge[],
): { edges: Edge[]; removedEdges: Edge[] } {
  let current = edges;
  const removedEdges: Edge[] = [];
  for (const node of nodes) {
    const data = node.data as {
      stepType?: string;
      inputs?: Record<string, { id: string }>;
      outputs?: Record<string, { id: string }>;
    };
    if (data.stepType !== DECOMPOSE_ENTITY_STEP_TYPE) continue;
    const socketIds = (sockets?: Record<string, { id: string }>) =>
      new Set(Object.values(sockets ?? {}).map((socket) => socket.id));
    const result = pruneEdgesForNodeSockets(
      current,
      node.id,
      socketIds(data.inputs),
      socketIds(data.outputs),
    );
    current = result.edges;
    removedEdges.push(...result.removedEdges);
  }
  return { edges: current, removedEdges };
}

export function nodesToValidationSteps(nodes: Node[]): ValidationStep[] {
  // Step-layout-group container nodes are decoration, not steps. Left in, each
  // would validate as an edgeless step of type "unknown" and light up as a
  // floating node — so they're dropped here, at the one choke point both the
  // editor badges and the publish path go through.
  const steps: ValidationStep[] = [];

  for (const node of nodes) {
    if (isStepLayoutGroupNode(node)) continue;

    const data = node.data as {
      stepType?: string;
      category?: string;
      label?: string;
      // Node-data sockets are MMGraphSocket (see step-node.tsx), which spells
      // the type `socketType` — NOT `type`, which is what _stepTypeData uses.
      // Reading `type` here silently yields undefined, and an undefined socket
      // type makes every fanout check quietly find nothing.
      inputs?: Record<
        string,
        {
          id: string;
          socketType?: string;
          required?: boolean;
          expression?: string;
        }
      >;
      outputs?: Record<string, { id: string; socketType?: string }>;
      _stepTypeData?: ListStepTypesResponse['stepTypes'][0];
    };

    steps.push({
      id: node.id,
      type: data.stepType ?? 'unknown',
      // Canvas label, so warnings that name a step read the way the user sees
      // it ("Split Document (V2)") rather than the type slug.
      name: data.label,
      category:
        (data.category as ValidationStep['category']) ??
        data._stepTypeData?.category ??
        'Utility',
      config: (data as Record<string, unknown>).config as
        Record<string, unknown> | undefined,
      // Socket `type` and `expression` are load-bearing for fanout-topology
      // warnings: an array-typed output landing on a scalar input is what makes
      // the engine fan out, and an expression replaces the stored edge feeding
      // its socket. Dropping either makes those checks silently find nothing.
      sockets: {
        inputs: data._stepTypeData
          ? data._stepTypeData.inputSockets.map((socket) => ({
              id: socket.id,
              name: socket.name,
              type: socket.type,
              required: socket.required ?? true,
              expression: data.inputs?.[socket.id]?.expression,
            }))
          : data.inputs
            ? Object.values(data.inputs).map((socket) => ({
                id: socket.id,
                name: socket.id,
                type: socket.socketType,
                required: socket.required ?? true,
                expression: socket.expression,
              }))
            : [],
        outputs: data._stepTypeData
          ? data._stepTypeData.outputSockets.map((socket) => ({
              id: socket.id,
              name: socket.name,
              type: socket.type,
            }))
          : data.outputs
            ? Object.values(data.outputs).map((socket) => ({
                id: socket.id,
                name: socket.id,
                type: socket.socketType,
              }))
            : [],
      },
    });
  }

  return steps;
}

export function edgesToValidationEdges(edges: Edge[]): ValidationEdge[] {
  return edges.map((edge) => ({
    id: String(edge.id),
    sourceStepId: edge.source,
    sourceSocketId: edge.sourceHandle ?? '',
    targetStepId: edge.target,
    targetSocketId: edge.targetHandle ?? '',
  }));
}

interface BoundaryStepNodeData extends Record<string, unknown> {
  label: string;
  stepType?: string;
  config?: Record<string, unknown>;
  inputs?: Record<string, NodeSocketRecordEntry>;
  outputs?: Record<string, NodeSocketRecordEntry>;
  _stepTypeData?: StepTypeData;
}

function readBoundaryPorts(node: Node): ActionPort[] {
  const data = node.data as BoundaryStepNodeData;
  const parsed = actionBoundaryConfigSchema.safeParse(data.config ?? {});
  return parsed.success ? parsed.data.ports : [];
}

function withBoundaryPorts(node: Node, ports: ActionPort[]): Node {
  const data = node.data as BoundaryStepNodeData;
  return {
    ...node,
    data: {
      ...data,
      config: { ...(data.config ?? {}), ports },
    },
  };
}

export function applyPortMutation<NodeT extends Node>(
  nodes: NodeT[],
  boundaryNodeId: string,
  mutate: (ports: ActionPort[]) => ActionPort[],
): NodeT[] {
  return nodes.map((node) =>
    node.id === boundaryNodeId
      ? (withBoundaryPorts(node, mutate(readBoundaryPorts(node))) as NodeT)
      : node,
  );
}

export function removeBoundaryPortsForEdges<NodeT extends Node>(
  nodes: NodeT[],
  removedEdges: Edge[],
): NodeT[] {
  const removalsByBoundary = new Map<string, Set<string>>();
  const addRemoval = (nodeId: string, portId: string) => {
    const removals = removalsByBoundary.get(nodeId) ?? new Set<string>();
    removals.add(portId);
    removalsByBoundary.set(nodeId, removals);
  };

  for (const edge of removedEdges) {
    // react-doctor-disable-next-line react-doctor/js-index-maps
    const source = nodes.find((node) => node.id === edge.source);
    // react-doctor-disable-next-line react-doctor/js-index-maps
    const target = nodes.find((node) => node.id === edge.target);
    if (
      source &&
      (source.data as BoundaryStepNodeData).stepType ===
        ACTION_INPUT_STEP_TYPE &&
      edge.sourceHandle
    ) {
      addRemoval(source.id, edge.sourceHandle);
    }
    if (
      target &&
      (target.data as BoundaryStepNodeData).stepType ===
        ACTION_OUTPUT_STEP_TYPE &&
      edge.targetHandle
    ) {
      addRemoval(target.id, edge.targetHandle);
    }
  }

  let nextNodes = nodes;
  for (const [nodeId, portIds] of removalsByBoundary) {
    nextNodes = applyPortMutation(nextNodes, nodeId, (ports) =>
      ports.filter((port) => !portIds.has(port.id)),
    );
  }
  return nextNodes;
}

function nodeToActionGraphStep(node: Node) {
  const data = node.data as BoundaryStepNodeData;
  return {
    id: node.id,
    type: data.stepType ?? 'unknown',
    name: data.label,
    config: data.config,
    sockets: {
      inputs:
        data._stepTypeData?.inputSockets?.map((socket) => ({
          id: socket.id,
          name: socket.name,
          type: socket.type,
          required: socket.required ?? true,
          description: socket.description,
        })) ??
        Object.entries(data.inputs ?? {}).map(([key, socket]) => ({
          id: socket.id,
          name: socket.name ?? key,
          type: socket.socketType ?? 'any',
          required: socket.required ?? true,
        })),
      outputs:
        data._stepTypeData?.outputSockets?.map((socket) => ({
          id: socket.id,
          name: socket.name,
          type: socket.type,
          required: true,
          description: socket.description,
        })) ??
        Object.entries(data.outputs ?? {}).map(([key, socket]) => ({
          id: socket.id,
          name: socket.name ?? key,
          type: socket.socketType ?? 'any',
          required: true,
        })),
    },
  };
}

function toSocketRecord(
  sockets: Array<{ id: string; name: string; type: string }>,
  ioType: IOType,
): Record<string, NodeSocketRecordEntry> {
  return Object.fromEntries(
    sockets.map((socket) => [
      socket.id,
      { id: socket.id, name: socket.name, ioType, socketType: socket.type },
    ]),
  );
}

/**
 * The graph's steps as the action-interface derivation wants them. Layout-group
 * containers aren't steps, so they're skipped in the same pass that maps.
 */
function collectActionGraphSteps(nodes: Node[]) {
  const steps = [];
  for (const node of nodes) {
    if (isStepLayoutGroupNode(node)) continue;
    steps.push(nodeToActionGraphStep(node));
  }
  return steps;
}

export function syncGraphDerivedActionSockets<NodeT extends Node>({
  nodes,
  edges,
}: {
  nodes: NodeT[];
  edges: Edge[];
}): { nodes: NodeT[]; errors: string[] } {
  const hasBoundary = nodes.some((node) => {
    const stepType = (node.data as BoundaryStepNodeData).stepType;
    return (
      stepType === ACTION_INPUT_STEP_TYPE ||
      stepType === ACTION_OUTPUT_STEP_TYPE
    );
  });
  if (!hasBoundary) return { nodes, errors: [] };

  const actionInterface = deriveWorkflowActionInterface({
    // Group containers aren't steps — keep them out of the interface derivation.
    // (They pass through `nextNodes` below untouched, like any non-boundary node.)
    steps: collectActionGraphSteps(nodes),
    // react-doctor-disable-next-line react-doctor/js-combine-iterations
    edges: edges
      .filter((edge) => edge.sourceHandle && edge.targetHandle)
      .map((edge) => ({
        id: String(edge.id),
        sourceStepId: edge.source,
        sourceSocketId: edge.sourceHandle ?? '',
        targetStepId: edge.target,
        targetSocketId: edge.targetHandle ?? '',
      })),
  });

  const nextNodes = nodes.map((node) => {
    const data = node.data as BoundaryStepNodeData;
    if (data.stepType === ACTION_INPUT_STEP_TYPE) {
      const outputSockets = actionInterface.inputs.map((socket) => ({
        ...socket,
        required: socket.required ?? false,
      }));
      return {
        ...node,
        data: {
          ...data,
          inputs: {},
          outputs: toSocketRecord(outputSockets, IOType.OUTPUT),
          _stepTypeData: {
            ...(data._stepTypeData ?? {
              type: data.stepType,
              name: data.label,
              description: '',
              category: 'Trigger',
              inputSockets: [],
              outputSockets: [],
            }),
            // action_input has no inputs; clear any previously hydrated
            // inputSockets so stale metadata can't resurface (mirrors the
            // action_output branch's `outputSockets: []`).
            inputSockets: [],
            outputSockets,
          },
        },
      };
    }
    if (data.stepType === ACTION_OUTPUT_STEP_TYPE) {
      const inputSockets = actionInterface.outputs.map((socket) => ({
        ...socket,
        required: socket.required ?? true,
      }));
      return {
        ...node,
        data: {
          ...data,
          inputs: toSocketRecord(inputSockets, IOType.INPUT),
          outputs: {},
          _stepTypeData: {
            ...(data._stepTypeData ?? {
              type: data.stepType,
              name: data.label,
              description: '',
              category: 'Utility',
              inputSockets: [],
              outputSockets: [],
            }),
            inputSockets,
            outputSockets: [],
          },
        },
      };
    }
    return node;
  });

  return {
    nodes: nextNodes as NodeT[],
    errors: actionInterface.errors.map((error) => error.message),
  };
}

export function disconnectNodeInGraph<NodeT extends Node>(
  nodes: NodeT[],
  edges: Edge[],
  nodeId: string,
  { removeNode }: { removeNode: boolean },
): { nodes: NodeT[]; edges: Edge[]; removedEdgeCount: number } {
  const removedEdges = edges.filter(
    (edge) => edge.source === nodeId || edge.target === nodeId,
  );
  const nextEdges = edges.filter(
    (edge) => edge.source !== nodeId && edge.target !== nodeId,
  );
  let nextNodes = removeBoundaryPortsForEdges(nodes, removedEdges);
  if (removeNode) {
    nextNodes = nextNodes.filter((node) => node.id !== nodeId);
  }
  const synced = syncGraphDerivedActionSockets({
    nodes: nextNodes,
    edges: nextEdges,
  });
  return {
    nodes: synced.nodes,
    edges: nextEdges,
    removedEdgeCount: removedEdges.length,
  };
}

/**
 * Reconstruct every step's real current input expression from the canvas —
 * manual override if set, otherwise edge-derived, exactly like
 * `graphToDefinition` — with one socket's expression replaced by a
 * not-yet-saved candidate value. A React Flow adapter over the shared
 * `buildManualOverrideValidationSteps` in `@erp/workflows`, which the chat
 * agent's `validate_socket_expression` tool runs too.
 */
export function buildManualOverrideValidationSteps(
  nodes: Node[],
  edges: Edge[],
  editingNodeId: string,
  editingSocketId: string,
  candidateExpression: string,
): WorkflowExpressionStep[] {
  return buildSharedManualOverrideValidationSteps(
    nodes.map((node) => {
      const data = node.data as {
        inputs?: Record<string, { id: string; socketType?: string }>;
        outputs?: Record<string, { id: string; socketType?: string }>;
        config?: Record<string, unknown>;
      };
      return {
        id: node.id,
        inputSockets: Object.values(data.inputs ?? {}).map((socket) => ({
          id: socket.id,
          type: socket.socketType,
        })),
        outputSockets: Object.values(data.outputs ?? {}).map((socket) => ({
          id: socket.id,
          type: socket.socketType,
        })),
        overrides: readManualExpressionOverrides(data.config),
      };
    }),
    edges.map((edge) => ({
      sourceStepId: edge.source,
      sourceSocketId: edge.sourceHandle ?? '',
      targetStepId: edge.target,
      targetSocketId: edge.targetHandle ?? '',
    })),
    {
      stepId: editingNodeId,
      socketId: editingSocketId,
      expression: candidateExpression,
    },
  );
}

/**
 * Monomorphize an Await (`fan_in`) node: read the concrete type of whatever is
 * wired into its `value` socket.
 *
 * `fan_in` is the only step whose output sockets depend on a WIRE rather than
 * on config — it is generic (`input<T> → array<T>`), the exact type-level
 * inverse of fanout. Returning `null` means `T` is still unresolved (nothing
 * wired, or the source socket is untyped), which the generator turns into "no
 * result socket yet" rather than an untyped `array<json>` that would let an
 * incompatible downstream wire pass publish validation.
 */
// Two source paths (override, edge) times the guards each needs, and none of
// them is a decision worth its own function: the branch count is the number of
// ways a Value input can be absent or untyped. Covered by the resolver tests in
// editorUtils.test.ts.
// fallow-ignore-next-line complexity
export function resolveFanInInputType(
  nodeId: string,
  nodes: Node[],
  edges: Edge[],
): SocketType | null {
  const awaitNode = nodes.find((node) => node.id === nodeId);

  // A manual override REPLACES the socket's edge, and the backend and runtime
  // both follow it — so reading only the React Flow edge left the canvas
  // stamping the Await from a source nothing else uses, or dropping the result
  // socket entirely for an override that supplies Value with no edge at all.
  // A `null` entry is an explicitly cleared override, which falls back to the
  // edge like any un-overridden socket.
  const overrideExpression = readManualExpressionOverrides(
    (awaitNode?.data as { config?: Record<string, unknown> } | undefined)
      ?.config,
  )[FAN_IN_INPUT_SOCKET_ID];

  // An override that references nothing (a literal, or a transform over
  // literals) yields no edge, so `T` stays unresolved — the same answer the
  // unwired case gives, and the honest one: a literal's type is not a socket's.
  const overrideSource = overrideExpression
    ? deriveWorkflowExpressionEdges([
        {
          id: nodeId,
          inputSockets: [
            { id: FAN_IN_INPUT_SOCKET_ID, expression: overrideExpression },
          ],
        },
      ])[0]
    : undefined;

  const source = overrideExpression
    ? overrideSource && {
        id: overrideSource.sourceStepId,
        handle: overrideSource.sourceSocketId,
      }
    : (() => {
        const incoming = edges.find(
          (edge) =>
            edge.target === nodeId &&
            edge.targetHandle === FAN_IN_INPUT_SOCKET_ID,
        );
        return (
          incoming && { id: incoming.source, handle: incoming.sourceHandle }
        );
      })();
  if (!source) return null;

  const sourceNode = nodes.find((node) => node.id === source.id);
  if (!sourceNode) return null;

  const socketType = getSocketTypeFromNode(sourceNode, source.handle, 'output');
  // 'any' is the editor's stand-in for "no type information", which for this
  // step is indistinguishable from not being wired at all.
  if (socketType === 'any') return null;

  // Parse rather than cast. `getSocketTypeFromNode` returns whatever string the
  // node data carries, and node data can come from a persisted definition or an
  // imported workflow — an unrecognized value cast straight through would be
  // stamped onto the Await's result socket and then fail validation somewhere
  // far from here. An unparseable type is simply unresolved.
  const parsed = SocketTypeSchema.safeParse(socketType);
  return parsed.success ? (parsed.data as SocketType) : null;
}

/**
 * Await output sockets for the editor's live regeneration paths, in the shape
 * the canvas stores. Wraps the shared generator so the editor, the save path
 * and template instantiation cannot disagree about the socket set.
 */
export function fanInOutputSocketsForNode(
  nodeId: string,
  config: Record<string, unknown>,
  nodes: Node[],
  edges: Edge[],
): Array<{ id: string; name: string; type: string; required: boolean }> {
  return generateFanInOutputSockets(
    config,
    resolveFanInInputType(nodeId, nodes, edges),
  ).map((socket) => ({
    id: socket.id,
    name: socket.name,
    type: socket.type,
    required: socket.required,
  }));
}
